// utils/dashboardUtils.js
// Shared utilities for dashboard components
import apiService from '../services/api';  // ✅ Add this import at the top

/**
 * Format hours to one decimal place
 */
export const formatHours = (hours) => {
  return (hours || 0).toFixed(1);
};

/**
 * Format currency to USD
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

/**
 * Format percentage to one decimal place
 */
export const formatPercentage = (value) => {
  return `${(value || 0).toFixed(1)}%`;
};

/**
 * Calculate profitability percentage
 */
export const calculateProfitability = (invoiced, incurred) => {
  if (!invoiced) return 0;
  return ((invoiced - incurred) * 100) / invoiced;
};

/**
 * Get profitability color classes based on percentage
 */
export const getProfitabilityColor = (profitability) => {
  if (profitability >= 30) return 'text-green-600 bg-green-50';
  if (profitability >= 15) return 'text-yellow-600 bg-yellow-50';
  return 'text-red-600 bg-red-50';
};

/**
 * Get productivity color classes based on rate
 */
export const getProductivityColor = (rate) => {
  if (rate >= 0.75) return 'text-green-600 bg-green-50';
  if (rate >= 0.5) return 'text-yellow-600 bg-yellow-50';
  return 'text-red-600 bg-red-50';
};

/**
 * Initialize default date range (YTD)
 */
export const getDefaultDateRange = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return {
    start_date: `${year}-01-01`,
    end_date: `${year}-${month}-${day}`,
    preset: 'ytd'
  };
};

/**
 * Initialize cycle month selector to current month
 */
export const getCurrentCycleMonth = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Sort data by a given key and direction
 */
export const sortData = (data, sortConfig) => {
  return [...data].sort((a, b) => {
    const aValue = a[sortConfig.key] || 0;
    const bValue = b[sortConfig.key] || 0;
    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
  });
};

/**
 * Format quarter label (e.g., "2024 Q1" -> "Q1")
 */
export const formatQuarterLabel = (quarter) => {
  const match = quarter.match(/Q\d/);
  return match ? match[0] : quarter;
};

/**
 * Set document title and favicon for dashboard pages
 */
export const setDocumentMeta = (title, faviconPath = '/favicon.png') => {
  document.title = title;
  
  let link = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.getElementsByTagName('head')[0].appendChild(link);
  }
  link.href = faviconPath;
};

/**
 * Global cache refresh handler for dashboards
 */
export const refreshDashboardCache = async (endpoint, refetchCallbacks) => {
  try {
    // ✅ Use apiService instead of raw fetch
    await apiService.post(endpoint);
    
    // Execute all refetch callbacks
    refetchCallbacks.forEach(callback => callback());
  } catch (error) {
    console.error('Cache refresh failed:', error);
    // Still refetch even if cache refresh fails
    refetchCallbacks.forEach(callback => callback());
  }
};